import { useCreation, useMemoizedFn, useUpdateEffect } from 'ahooks';
import { useEffect, useRef, useState } from 'react';
import type { LogicalPos } from '../services/LayoutStore';
import { LayoutStore } from '../services/LayoutStore';
import { isIOSSafari } from '../utils/common';
import { waitForScrollEnd, waitTwoFrames } from '../utils/scrollUtils';

/**
 * iOS Safari 下的 overscan 值（px）。
 * 减小 overscan 可降低同时存活的 canvas 数，从而降低 WebKit IOSurface 总内存峰值，
 * 防止进程预算耗尽导致标签页白屏崩溃。
 * 400px 在正常滑动速度下不会出现白屏，极快速滑动时可能短暂空白（可接受的权衡）。
 */
const OVERSCAN_PX = isIOSSafari ? 400 : 1000;

/**
 * 滚动边界吸附判定的像素容差（单位：px）。
 *
 * 用途：判断“到顶/到底”时不要求 scrollTop 严格等于 0 / maxScrollTop，
 * 避免因小数滚动、惯性回弹、缩放等导致的 1-2px 抖动，让“最后一页/第一页”的页码语义更稳定。
 */
const SCROLL_SNAP_PX = 4;

export interface UsePdfLayoutProps {
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js pdfDoc
    pdfDoc: any;
    totalPages: number;
    scale: number;
    enabled: boolean;
    containerRef: React.RefObject<HTMLDivElement>;
    initialPageNum?: number; // 新增：初始页码参数
}

export function usePdfLayout(props: UsePdfLayoutProps) {
    const { totalPages, scale, enabled, containerRef, initialPageNum } = props;

    // 缓存已测量的页面高度，在 LayoutStore 重建时（全屏切换触发 enabled 变化）立即恢复，
    // 避免所有高度归零导致布局从零收敛带来的抖动与跳位
    const heightCacheRef = useRef<{ totalPages: number; heights: number[] } | null>(null);

    // 1. 创建 LayoutStore 实例
    const store = useCreation(() => {
        if (enabled && totalPages > 0) {
            const s = new LayoutStore(totalPages);
            // 若缓存与当前文档（totalPages）匹配，立即恢复所有已知高度
            const cache = heightCacheRef.current;
            if (cache && cache.totalPages === totalPages) {
                // 用 batchUpdate 包裹，所有写入完成后仅触发一次 notifyOnce，避免多余 RAF
                s.batchUpdate(() => {
                    cache.heights.forEach((h, i) => {
                        if (h > 0) s.updateHeight(i, h);
                    });
                });
            } else {
                // 新文档：创建空缓存
                heightCacheRef.current = { totalPages, heights: new Array(totalPages).fill(0) };
            }
            return s;
        }
        return null;
    }, [enabled, totalPages]);

    // 2. 状态：可见范围、总高度、当前页索引
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: 1 });
    const [totalHeight, setTotalHeight] = useState(0);
    const [centerPageIndex, setCenterPageIndex] = useState(0);
    // 边界吸附：用于“滚动到底/到顶”时的页码语义修正（避免中心页口径导致最后一页无法成为 current）
    const [isAtTop, setIsAtTop] = useState(true);
    const [isAtBottom, setIsAtBottom] = useState(false);
    const lastScrollPosRef = useRef<LogicalPos | null>(null);
    const isAdjustingRef = useRef(false);
    /** 最后一次用户 scroll 事件的时间戳（ms），用于 compensate 滚动守卫 */
    const lastScrollTimeRef = useRef<number>(0);
    /** 跳转目标页索引（0-based）。非 null 时 store 高度变更只做”目标页锚定”，不执行 compensate，避免抖动 */
    const jumpTargetIndexRef = useRef<number | null>(null);
    /** 持久化当前跳转的 AbortController，新跳转发起时 abort 前一次，防止并发竞态 */
    const jumpAbortControllerRef = useRef<AbortController | null>(null);
    /** 上一次 scale 值，用于缩放时的 scrollTop 比例补偿 */
    const prevScaleRef = useRef<number>(scale);
    /** 上次 scroll 事件时的 scrollTop，用于过滤纯横向滚动事件，避免误触发翻页 */
    const prevScrollTopRef = useRef<number>(0);
    /**
     * scale 变化触发 scrollTop 比例补偿期间置为 true，rAF 后重置。
     * store.subscribe 中 compensate() 检查此标记，缩放过渡期间跳过，
     * 避免链路 B（canvas 高度更新 → compensate → 二次 scrollTop 调整）引发多页抖动。
     */
    const isScaleAdjustingRef = useRef(false);
    // 记录是否已完成全屏后的初始定位，避免重复跳转
    const hasInitialScrolledRef = useRef(false);

    // 当 store 实例切换（enabled/totalPages 变化）时，显式 dispose 旧实例以取消挂起的 rAF
    useEffect(() => {
        return () => {
            store?.dispose();
        };
    }, [store]);

    // 重置初始定位标记（当禁用或总页数变化时）
    useEffect(() => {
        if (!enabled) {
            hasInitialScrolledRef.current = false;
        }
    }, [enabled, totalPages]);

    // 3. 辅助方法：更新所有状态
    const updateLayoutState = useMemoizedFn((forceVisibleRangeOnly = false) => {
        if (!(store && containerRef.current)) {
            return;
        }

        const container = containerRef.current;
        const scrollTop = container.scrollTop;
        const viewportHeight = container.clientHeight;
        const s = scale || 1;

        // 获取逻辑位置（中心点）
        const pos = store.getLogicalPos(scrollTop, viewportHeight, s);

        // 1. 更新逻辑坐标快照 (仅在非调整状态下更新锚点，用于后续的高度变化补偿)
        if (!(isAdjustingRef.current || forceVisibleRangeOnly)) {
            lastScrollPosRef.current = pos;
        }

        // 2. 更新页码索引
        // 仅在非调整状态下更新，避免手动跳转过程中 intermediate 状态干扰 UI
        if (!isAdjustingRef.current && centerPageIndex !== pos.pageIndex) {
            setCenterPageIndex(pos.pageIndex);
        }

        // 3. 更新可见范围 (无论是否在调整中都要更新，否则滚动中会白屏)
        // iOS Safari 使用较小的 overscan，降低并发 canvas 数，防止 IOSurface 超限崩溃
        const range = store.getVisibleRange(scrollTop, viewportHeight, s, OVERSCAN_PX);
        setVisibleRange((prev) => {
            if (prev.start === range.start && prev.end === range.end) {
                return prev;
            }
            return range;
        });

        // 4. 更新总高度
        const totalH = store.getTotalHeight(s);
        setTotalHeight(totalH);

        // 5. 更新“到顶/到底”状态：只在状态发生变化时 setState，避免滚动过程高频 re-render
        const maxScrollTop = Math.max(0, totalH - viewportHeight);
        const nextAtTop = scrollTop <= SCROLL_SNAP_PX;
        const nextAtBottom = maxScrollTop - scrollTop <= SCROLL_SNAP_PX;
        setIsAtTop((prev) => (prev === nextAtTop ? prev : nextAtTop));
        setIsAtBottom((prev) => (prev === nextAtBottom ? prev : nextAtBottom));
    });

    // 4. 锚点补偿核心逻辑
    const compensate = useMemoizedFn(() => {
        if (!(store && containerRef.current && lastScrollPosRef.current) || isAdjustingRef.current) {
            return;
        }

        const { pageIndex, offsetPercent } = lastScrollPosRef.current;
        const container = containerRef.current;
        const viewportHeight = container.clientHeight;
        const s = scale || 1;

        const pageTop = store.getPageTop(pageIndex, s);
        const pageHeight = store.getPageHeight(pageIndex, s);

        // 补偿计算：保持之前的逻辑位置在视口中的相对位置
        const targetScrollTop = pageTop + pageHeight * offsetPercent - viewportHeight / 2;

        const diff = Math.abs(container.scrollTop - targetScrollTop);
        // 只有当差异在一定范围内且不是微小差异时才补偿，避免无限震荡或过度跳转
        if (diff > 0.5 && diff < 1500) {
            // 滚动守卫：若用户仍在惯性滚动中（距上次 scroll 事件 < 100ms），
            // 等待滚动停止后再写入 scrollTop，避免打断 momentum scroll
            const msSinceLastScroll = performance.now() - lastScrollTimeRef.current;
            const doWrite = () => {
                if (!containerRef.current) return;
                isAdjustingRef.current = true;
                containerRef.current.scrollTop = targetScrollTop;
                waitTwoFrames().then(() => {
                    isAdjustingRef.current = false;
                    updateLayoutState();
                });
            };
            if (msSinceLastScroll < 100) {
                waitForScrollEnd(container).then(doWrite);
            } else {
                doWrite();
            }
        }
    });

    // 5. 监听 Store 变更 (高度收敛)
    useEffect(() => {
        if (!store) {
            return;
        }
        const unsubscribe = store.subscribe(() => {
            // 缩放 scrollTop 补偿期间跳过 compensate，避免链路 B（高度更新 → compensate → 二次 scrollTop 调整）引发多页抖动
            if (isScaleAdjustingRef.current) return;

            const container = containerRef.current;
            const targetIdx = jumpTargetIndexRef.current;
            // 跳转期间：只做目标页锚定，不执行 compensate，避免与纠偏/页顶对齐冲突导致抖动
            if (targetIdx !== null && container) {
                const s = scale || 1;
                const targetTop = Math.max(0, store.getPageTop(targetIdx, s));
                container.scrollTop = targetTop;
                const range = store.getVisibleRange(targetTop, container.clientHeight, s);
                setVisibleRange(range);
                setTotalHeight(store.getTotalHeight(s));
                updateLayoutState(true); // 仅同步可见范围等，不更新 lastScrollPosRef
                return;
            }
            // Store 变更时，如果正在跳转中，只同步可见范围，不更新锚点
            updateLayoutState(isAdjustingRef.current);
            if (!isAdjustingRef.current) {
                compensate();
            }
        });
        return () => {
            unsubscribe();
        };
    }, [store, updateLayoutState, compensate, scale]);

    // 6. 监听滚动与缩放 (使用原生事件以获得更高频率的反馈)
    useEffect(() => {
        const container = containerRef.current;
        if (!(container && enabled)) {
            return;
        }

        // 初始化上次 scrollTop，避免首次事件被误判为纯横向滚动
        prevScrollTopRef.current = container.scrollTop;

        const onScroll = () => {
            const newScrollTop = container.scrollTop;
            // 纯横向滚动（scrollTop 未变化）不触发布局更新：
            // 1. 避免 lastScrollTimeRef 被横向滚动刷新，导致 compensate 守卫延迟
            // 2. 避免全屏垂直模式下横向滚动附带的微小 scrollTop 抖动（0-2px）在页面边界处误触发 centerPageIndex 更新 → 翻页
            if (newScrollTop === prevScrollTopRef.current) {
                return;
            }
            prevScrollTopRef.current = newScrollTop;
            lastScrollTimeRef.current = performance.now();
            updateLayoutState(isAdjustingRef.current);
        };

        container.addEventListener('scroll', onScroll, { passive: true });
        return () => container.removeEventListener('scroll', onScroll);
    }, [containerRef, enabled, updateLayoutState]);

    // 6b. 监听容器尺寸变化（全屏切换后 Safari 等布局会变化，需同步可见范围与总高度）
    // 使用 rAF 节流，避免全屏过渡期间多次 resize 导致重复 setState
    useEffect(() => {
        const container = containerRef.current;
        if (!(container && enabled)) {
            return;
        }
        let rafId: number | null = null;
        const ro = new ResizeObserver(() => {
            if (rafId !== null) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                updateLayoutState(isAdjustingRef.current);
            });
        });
        ro.observe(container);
        return () => {
            if (rafId !== null) cancelAnimationFrame(rafId);
            ro.disconnect();
        };
    }, [containerRef, enabled, updateLayoutState]);

    // 缩放变化时：先按比例补偿 scrollTop，再同步布局状态
    // 修复：scale 从 S1 → S2 时，若不调整 scrollTop，视口中心会漂移到错误位置
    // 正确公式：scrollTop_new = scrollTop_old × (S2 / S1)
    // 注意：跳转期间（jumpTargetIndexRef 非 null）不做比例补偿，避免与锚定逻辑冲突
    useUpdateEffect(() => {
        const prevScale = prevScaleRef.current;
        const container = containerRef.current;
        let didAdjustScrollTop = false;
        if (container && prevScale > 0 && scale > 0 && prevScale !== scale && jumpTargetIndexRef.current === null) {
            // 标记缩放调整进行中：store.subscribe 回调期间将跳过 compensate，
            // 防止链路 B（canvas 高度更新 → compensate → 二次 scrollTop 调整）引发多页抖动
            isScaleAdjustingRef.current = true;
            const ratio = scale / prevScale;
            container.scrollTop = Math.round(container.scrollTop * ratio);
            didAdjustScrollTop = true;
        }
        prevScaleRef.current = scale;
        if (didAdjustScrollTop) {
            // 等下一帧页面高度已更新后，再同步布局状态并放行 compensate
            requestAnimationFrame(() => {
                isScaleAdjustingRef.current = false;
                updateLayoutState(false);
            });
        } else {
            updateLayoutState(isAdjustingRef.current);
        }
    }, [scale, store]);

    // 7. 初始同步
    useEffect(() => {
        if (enabled && store) {
            updateLayoutState();
        }
    }, [enabled, store, updateLayoutState]);

    // 7b. 批量预取全部页高（pdfDoc 就绪后立即执行）
    // 作用：消除"首次快速滚动因默认高度 800px 与真实高度误差导致的视口跳变"
    // 实现：batchUpdate 包裹所有 updateHeight，整批完成后仅触发一次 notifyOnce
    const { pdfDoc } = props;
    useEffect(() => {
        if (!pdfDoc) return;
        if (!store) return;
        if (!enabled) return;
        if (totalPages <= 0) return;
        let cancelled = false;
        const prefetch = async () => {
            const updates: Array<{ index: number; height: number }> = [];
            for (let i = 1; i <= totalPages; i++) {
                if (cancelled) return;
                try {
                    const page = await pdfDoc.getPage(i);
                    if (cancelled) return;
                    const vp = page.getViewport({ scale: 1 });
                    updates.push({ index: i - 1, height: vp.height });
                } catch {
                    // 单页预取失败时跳过，不影响其余页
                }
            }
            if (cancelled || updates.length === 0) return;
            store.batchUpdate(() => {
                for (const { index, height } of updates) {
                    store.updateHeight(index, height);
                }
            });
        };
        prefetch();
        return () => {
            cancelled = true;
        };
    }, [pdfDoc, store, enabled, totalPages]);

    // 8. 暴露出供外部使用的接口
    const updatePageHeight = useMemoizedFn((index: number, height: number) => {
        store?.updateHeight(index, height);
        // 持久化到缓存，LayoutStore 重建时可立即恢复
        const cache = heightCacheRef.current;
        if (cache && cache.totalPages === totalPages && index >= 0 && index < cache.heights.length) {
            cache.heights[index] = height;
        }
    });

    const scrollToPage = useMemoizedFn((index: number, behavior: ScrollBehavior = 'smooth') => {
        if (!(store && containerRef.current) || index < 0 || index >= totalPages) {
            return;
        }

        const container = containerRef.current;
        const s = scale || 1;

        // 跳转期间用目标页锚定替代 compensate，避免高度变更导致抖动（见 docs/SCROLL_JUMP_JITTER_ANALYSIS.md）
        jumpTargetIndexRef.current = index;

        // 确保跳转时目标位置的高度已经测量过，否则先给一个预估高度
        const pageTop = store.getPageTop(index, s);
        // 目标策略：最终要让“目标页的顶部”与视口顶部对齐，避免出现上一页尾部露出 / 目标页顶部被遮住
        const targetTop = Math.max(0, pageTop);

        isAdjustingRef.current = true;

        // 关键：立即更新可见范围，让目标页面瞬间开始渲染
        const immediateRange = store.getVisibleRange(targetTop, container.clientHeight, s);
        setVisibleRange(immediateRange);

        container.scrollTo({ top: targetTop, behavior });

        // ===== 迭代纠偏（不改主交互，只在跳转后自动收敛到目标页）=====
        // 目的：当大量前置页仍为默认高度时，targetTop 会有累计误差，首次滚动可能落在目标页前几页（如 120 -> 118/109）。
        // 方法：等待滚动停止后（waitForScrollEnd），读取”实际中心页”，用页差 * 估算页高 修正 scrollTop，最多两次。
        const maxIter = 2;
        let iter = 0;
        // 中止前一次未完成的跳转链路，防止并发竞态：多次快速跳转时旧链路的 jumpTargetIndexRef 被覆盖
        jumpAbortControllerRef.current?.abort();
        const ac = new AbortController();
        jumpAbortControllerRef.current = ac;

        const clearJumpTargetAndAdjusting = () => {
            jumpTargetIndexRef.current = null;
            isAdjustingRef.current = false;
            updateLayoutState();
        };

        // 已收敛到目标页：执行页顶对齐收尾，然后解锁跳转态
        const handleAligned = async (c: HTMLElement, beforeTop: number): Promise<void> => {
            const finalTop = Math.max(0, store.getPageTop(index, s));
            if (Math.abs(beforeTop - finalTop) > 2) {
                isAdjustingRef.current = true;
                setVisibleRange(store.getVisibleRange(finalTop, c.clientHeight, s));
                c.scrollTo({ top: finalTop, behavior: 'auto' });
            }
            await waitTwoFrames();
            if (!ac.signal.aborted) clearJumpTargetAndAdjusting();
        };

        // 未收敛：计算修正位置并滚动，准备下一轮纠偏
        const handleMisaligned = async (c: HTMLElement, actualIdx: number, deltaPages: number): Promise<void> => {
            if (iter >= maxIter) {
                jumpTargetIndexRef.current = null;
                return;
            }
            // deltaPages < 0：视口中心越过目标页，直接对齐页顶避免回跳抖动
            const stepHeight = store.getPageHeight(actualIdx, s) || store.getDefaultHeight() * s;
            const nextTop =
                deltaPages < 0
                    ? Math.max(0, store.getPageTop(index, s))
                    : Math.max(0, c.scrollTop + deltaPages * stepHeight);
            isAdjustingRef.current = true;
            setVisibleRange(store.getVisibleRange(nextTop, c.clientHeight, s));
            c.scrollTo({ top: nextTop, behavior: 'auto' });
            await waitTwoFrames();
            if (ac.signal.aborted) return;
            isAdjustingRef.current = false;
            updateLayoutState();
            iter += 1;
            await runCorrection(); // 递归，最多 maxIter 次
        };

        const runCorrection = async (): Promise<void> => {
            try {
                await waitForScrollEnd(container, ac.signal);
                if (ac.signal.aborted) return;
                const c = containerRef.current;
                if (!c) {
                    clearJumpTargetAndAdjusting();
                    return;
                }
                const beforeTop = c.scrollTop;
                const pos = store.getLogicalPos(c.scrollTop, c.clientHeight, s);
                const deltaPages = index - pos.pageIndex;
                if (deltaPages === 0) {
                    await handleAligned(c, beforeTop);
                } else {
                    await handleMisaligned(c, pos.pageIndex, deltaPages);
                }
            } catch {
                if (!ac.signal.aborted) clearJumpTargetAndAdjusting();
            }
        };

        // 兜底：4秒后强制解除跳转态，防止 waitForScrollEnd 在极端情况下永不 resolve
        const fallbackTimer = setTimeout(() => {
            ac.abort();
            if (jumpTargetIndexRef.current === index) clearJumpTargetAndAdjusting();
        }, 4000);

        runCorrection().finally(() => clearTimeout(fallbackTimer));

        return () => {
            ac.abort();
            clearTimeout(fallbackTimer);
        };
    });

    // 9. 全屏初始定位监听：一旦 Store 中有了有效高度，且还未进行过初始定位，则执行跳转
    useEffect(() => {
        if (enabled && store && !hasInitialScrolledRef.current) {
            const unsubscribe = store.subscribe(() => {
                if (hasInitialScrolledRef.current) {
                    return;
                }

                // 检查是否已经有了基本的布局数据（至少当前页或前几页有了高度）
                const totalH = store.getTotalHeight(1);
                if (totalH > 0) {
                    hasInitialScrolledRef.current = true;
                    // 等两帧布局稳定后再跳转，避开 React 渲染冲突（替代 setTimeout(0)）
                    waitTwoFrames().then(() => {
                        const target = initialPageNum || 1;
                        scrollToPage(target - 1, 'auto');
                    });
                }
            });
            return () => {
                unsubscribe();
            };
        }
        return undefined;
    }, [enabled, store, scrollToPage, initialPageNum]);

    return {
        visibleRange,
        totalHeight,
        centerPageIndex,
        isAtTop,
        isAtBottom,
        updatePageHeight,
        scrollToPage,
        getPageTop: (index: number) => store?.getPageTop(index, scale) || 0,
        getPageHeight: (index: number) => store?.getPageHeight(index, scale) || 0,
    };
}
